import { Link } from "@inertiajs/react";
import { Copy, CornerLeftUp, Download, Eye, Link2, Lock, MoreHorizontal, Plus, QrCode } from "lucide-react";
import QrCodeDialog from "@/Components/QrCodeDialog";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/Components/ui/dropdown-menu";
import NodeTypeIcon from "@/Components/Passport/NodeTypeIcon";
import Snowflake from "@/Components/Snowflake";
import StatusGlyph from "@/Components/Passport/StatusGlyph";
import { isEditable, normalizeStatus } from "@/lib/publishStatus";
import { relativeTime } from "@/lib/serverTime";

/** The strip under the header: where the passport stands, and what to do about it. */
const STRIP = 'mt-4 flex min-h-9 flex-wrap items-center gap-x-2.5 gap-y-1 rounded-md bg-zinc-50 px-3 py-1.5 text-[12.5px] text-zinc-600 ring-1 ring-inset ring-zinc-200';

/**
 * The passport overview's header (C2): where the passport sits (parent, level,
 * ontology), its title with the copy-link and company ID affordances, its
 * snowflake, schema and last update, the actions (status, QR code, more), and
 * the strip under it that says where the passport stands: a released
 * passport's lock and its way to Revise, a revision in progress, an archived
 * passport's finality, or a read-only passport's reason.
 *
 * Rendered as a fragment: the editor places it, with PassportTabs, inside its
 * own padded white band. No routes and no requests in here; the container
 * hands in the links and the handlers.
 *
 * - `passport`: the passport read (`friendly_id`, `snowflake`,
 *   `parent_snowflake`, `ancestors`, `meta`, `publish_status`,
 *   `release_number`, `unreleased_changes`).
 * - `node`: its level, `ontology.data[currentNode]`; `ontology`: `name`,
 *   `version`, `latest_version`.
 * - `timeZone`: the app's timezone, for "Updated 12m ago".
 * - `parentHref`, `ontologyHref`, `publicUrl`: the parent passport, the
 *   ontology's root, the public page (the QR code encodes it).
 * - `previewUrl`: the team's preview of the viewer drawn from live values,
 *   linked as "Preview changes" while the passport is draft or revising —
 *   the public page shows the last release, or nothing before the first.
 *   No link without one (the marketing vignettes hand in none).
 * - `statusControl`: the status menu (the editor's PublishStatusControl).
 * - `presence`: who else has the passport open (the editor's PresenceFaces),
 *   first among the actions; nothing without it.
 * - `companyIdControl(trigger)`: wraps the "+ Company ID" button (shown while
 *   the passport has none) in the editor's popover; bare without it.
 * - `onCopyLink`, `onCopySnowflake`, `onExportChangelog` (disabled unless
 *   `canExportChangelog`): the copy button and the More menu.
 * - `onRevise`, `reviseBlockedReason`: the released banner's Revise
 *   (disabled, with the reason as its title, when not null).
 * - `readOnlyReason`: shows the read-only banner when the passport is not
 *   locked by its status (released, archived) and cannot be written.
 * - `titleAs`: the title's element, the page's `h1` unless told otherwise
 *   (the marketing site's vignettes, which show this header inside a page
 *   that has its own h1, render it as a paragraph).
 * - `linked` (true unless told otherwise): false keeps the parent and
 *   ontology crumbs as anchors without an href, and `dayFirst` writes an
 *   older "Updated" date day first; both for the marketing site's vignettes,
 *   which link nowhere (R-6) and read dates day first (1d).
 */
export default function PassportHeader({
    passport,
    node,
    ontology,
    timeZone,
    parentHref,
    ontologyHref,
    publicUrl,
    previewUrl = null,
    statusControl = null,
    presence = null,
    companyIdControl,
    onCopyLink,
    onCopySnowflake,
    onExportChangelog,
    canExportChangelog = false,
    onRevise,
    reviseBlockedReason = null,
    readOnlyReason = null,
    titleAs: Title = 'h1',
    linked = true,
    dayFirst = false,
}) {
    // An anchor with no href when unlinked: the same markup, nothing to follow.
    const Crumb = linked ? Link : 'a';

    const title = passport.friendly_id || passport.snowflake;
    const parent = (passport.ancestors ?? []).at(-1) ?? null;
    // Its OWN status: a released ancestor does not lock its children.
    const status = normalizeStatus(passport.publish_status);
    const releaseNumber = Number(passport.release_number ?? 0);
    // Values the public does not see yet exist only while draft or revising.
    const previewable = Boolean(previewUrl) && isEditable(status);

    const companyIdButton = (
        <button
            type="button"
            aria-label="Assign company ID"
            className="inline-flex h-6 items-center gap-1 rounded-md border border-dashed border-zinc-300 px-2 text-[12px] font-medium text-zinc-500 transition-colors hover:border-zinc-400 hover:text-zinc-900 disabled:pointer-events-none disabled:opacity-50"
        >
            <Plus className="size-3" aria-hidden="true" />Company ID
        </button>
    );

    return (
        <>
            <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
                <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-1.5 text-[12.5px] text-zinc-500">
                        {passport.parent_snowflake && (
                            <>
                                <Crumb
                                    href={linked ? parentHref : undefined}
                                    className="inline-flex items-center gap-1 transition-colors hover:text-zinc-900"
                                >
                                    <CornerLeftUp className="size-3.5 text-zinc-400" aria-hidden="true" />
                                    {parent ? (
                                        <span className="font-mono">{parent.friendly_id || parent.snowflake}</span>
                                    ) : 'Parent passport'}
                                </Crumb>
                                <span className="text-zinc-300" aria-hidden="true">/</span>
                            </>
                        )}
                        <span className="inline-flex items-center gap-1">
                            <NodeTypeIcon type={node?.type} isRoot={node?.is_root} />
                            {node?.name}
                        </span>
                        <span className="text-zinc-300" aria-hidden="true">·</span>
                        <Crumb
                            href={linked ? ontologyHref : undefined}
                            className="transition-colors hover:text-zinc-900"
                        >
                            {ontology.name}
                        </Crumb>
                    </div>
                    <div className="mt-1.5 flex flex-wrap items-center gap-2.5">
                        <Title className="break-all font-mono text-[22px] font-semibold tracking-[-0.02em] text-zinc-950 tabular-nums">{title}</Title>
                        <button
                            type="button"
                            onClick={() => onCopyLink?.()}
                            className="inline-flex size-7 items-center justify-center rounded-md text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-900"
                            aria-label="Copy public link"
                            title="Copy public link"
                        >
                            <Link2 className="size-4" aria-hidden="true" />
                        </button>
                        {!passport.friendly_id && (companyIdControl ? companyIdControl(companyIdButton) : companyIdButton)}
                    </div>
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px] text-zinc-500">
                        {/* Always here, even when the title is the snowflake
                            itself: the heading is text, this is the copy
                            affordance and the one place the id is labelled. */}
                        <Snowflake id={passport.snowflake} />
                        <span className="text-zinc-300" aria-hidden="true">·</span>
                        {ontology.version != null && (
                            <span
                                className="inline-flex items-center gap-1"
                                title={`This passport renders the fields of ontology version ${ontology.version}, the schema it was issued under.`
                                    + (ontology.latest_version > ontology.version
                                        ? ` Newer versions (up to v${ontology.latest_version}) exist; passports issued now use the latest version.`
                                        : ' This is the latest published version.')}
                            >
                                Schema v{ontology.version}
                                {ontology.latest_version > ontology.version && (
                                    <span className="inline-flex items-center gap-1 text-amber-700">
                                        <span className="size-1.5 rounded-full bg-amber-500" aria-hidden="true" />
                                        v{ontology.latest_version} available
                                    </span>
                                )}
                            </span>
                        )}
                        {passport.meta?.updated_at && (
                            <>
                                {ontology.version != null && <span className="text-zinc-300" aria-hidden="true">·</span>}
                                <span className="tabular-nums" title={passport.meta.updated_at}>
                                    Updated {relativeTime(passport.meta.updated_at, timeZone, undefined, { dayFirst })}
                                </span>
                            </>
                        )}
                    </div>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                    {presence}
                    {previewable && (
                        // A new tab: the preview is the public viewer's full
                        // page, and the editor stays where it was.
                        <a
                            href={previewUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="The public page as it will look once released"
                            className="inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-[13px] font-medium text-zinc-700 ring-1 ring-inset ring-zinc-200 transition-colors hover:bg-zinc-50 hover:text-zinc-900"
                        >
                            <Eye className="size-4" aria-hidden="true" />
                            Preview changes
                            <span className="sr-only"> (opens in a new tab)</span>
                        </a>
                    )}
                    {statusControl}
                    <QrCodeDialog
                        url={publicUrl}
                        fileBaseName={`passport-qr-${passport.snowflake}`}
                        title="Passport QR Code"
                    >
                        <button
                            type="button"
                            aria-label="QR code"
                            title="QR code"
                            className="inline-flex size-8 items-center justify-center rounded-md text-zinc-600 ring-1 ring-inset ring-zinc-200 transition-colors hover:bg-zinc-50 hover:text-zinc-900"
                        >
                            <QrCode className="size-4" aria-hidden="true" />
                        </button>
                    </QrCodeDialog>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button
                                type="button"
                                aria-label="More actions"
                                className="inline-flex size-8 items-center justify-center rounded-md text-zinc-600 ring-1 ring-inset ring-zinc-200 transition-colors hover:bg-zinc-50 hover:text-zinc-900"
                            >
                                <MoreHorizontal className="size-4" aria-hidden="true" />
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56 text-[13px]">
                            <DropdownMenuItem onSelect={() => onCopyLink?.()}>
                                <Link2 className="mr-2 size-3.5" aria-hidden="true" />Copy public link
                            </DropdownMenuItem>
                            <DropdownMenuItem onSelect={() => onCopySnowflake?.()}>
                                <Copy className="mr-2 size-3.5" aria-hidden="true" />Copy snowflake
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem disabled={!canExportChangelog} onSelect={() => onExportChangelog?.()}>
                                <Download className="mr-2 size-3.5" aria-hidden="true" />Export changelog (CSV)
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>

            {status === 'released' && (
                <div role="status" className={STRIP}>
                    <Lock className="size-3.5 text-zinc-500" aria-hidden="true" />
                    <span>
                        <span className="font-medium text-zinc-900">{releaseNumber ? `Release #${releaseNumber} is locked.` : 'This release is locked.'}</span>
                        {' '}Revise it to change values; the public keeps this release until you release again.
                    </span>
                    <button
                        type="button"
                        onClick={() => onRevise?.()}
                        disabled={reviseBlockedReason !== null}
                        title={reviseBlockedReason ?? undefined}
                        className="ml-auto font-medium text-zinc-900 underline-offset-2 hover:underline disabled:text-zinc-400 disabled:no-underline"
                    >
                        Revise
                    </button>
                </div>
            )}
            {status === 'revising' && (
                <div role="status" className={STRIP}>
                    <StatusGlyph status="revising" decorative />
                    <span>
                        <span className="font-medium text-zinc-900">{releaseNumber ? `Revising release #${releaseNumber}.` : 'Revising.'}</span>
                        {' '}The public keeps seeing it until you release again.
                    </span>
                    {passport.unreleased_changes && (
                        <span className="ml-auto inline-flex items-center gap-1.5 font-medium text-blue-700">
                            <span className="size-1.5 rounded-full bg-blue-500" aria-hidden="true" />
                            Unreleased changes
                        </span>
                    )}
                </div>
            )}
            {status === 'archived' && (
                <div role="status" className={STRIP}>
                    <Lock className="size-3.5 text-zinc-500" aria-hidden="true" />
                    <span>
                        <span className="font-medium text-zinc-900">Archived.</span>
                        {' '}It is final; its values can't be edited.
                    </span>
                </div>
            )}
            {!['released', 'archived'].includes(status) && readOnlyReason && (
                <div role="status" className={STRIP}>
                    <Lock className="size-3.5 text-zinc-500" aria-hidden="true" />
                    <span>{`Read-only — ${readOnlyReason}`}</span>
                </div>
            )}
        </>
    );
}
